import { useQuery, type UseQueryResult } from 'react-query'

import type { AgentPostTextTheme } from 'features/ask-yogi/types'

/**
 * Reads a single sentence's resolved theme/sentiment from the react-query
 * cache. The data is populated in the same batched request per message as
 * `useCitationData`, seeded by `useCitationsData` at the
 * `['citation-theme', projectId, id]` key this hook subscribes to. This
 * hook never fetches on its own (`enabled: false`).
 *
 * `data` is `undefined` while the batch is in flight, `null` once the batch
 * resolves without this post_text_id (no visible theme, or the citation
 * didn't name one), or the resolved theme otherwise.
 */
export function usePostTextTheme(
  projectId: string,
  postTextId: string | null,
): UseQueryResult<AgentPostTextTheme | null> {
  return useQuery<AgentPostTextTheme | null>(
    ['citation-theme', projectId, postTextId],
    // Never invoked: enabled is false, so the value only ever comes from
    // the cache seeded by useCitationsData.
    () =>
      Promise.reject(
        new Error('post-text theme is seeded by useCitationsData'),
      ),
    {
      enabled: false,
      staleTime: Infinity,
      cacheTime: Infinity,
    },
  )
}
